import { useEffect, useRef } from 'react';
import { clearAutoplayTimers } from './stateGuards';
import { L } from '../utils/logger';

type Track = { url: string; role?: string };

interface Options {
  maxStartMs?: number; // how long to wait for first advance before arming gesture
}

/**
 * Attempts autoplay on scope change; if blocked or taking too long, arms a one-time
 * global user-gesture handler to resume the AudioContext and call play().
 * Automatically disarms after successful start or on unmount/scope change.
 */
export default function useAutoplayGuard(
  mix: any,
  scopeId: string | number | null | undefined,
  tracks: Track[] | undefined,
  opts: Options = {}
) {
  const { maxStartMs = 1500 } = opts;

  const autoPlayedScopeRef = useRef<string | number | null>(null);
  const gestureArmedRef = useRef(false);
  const resumeOnceRef = useRef<((e?: any) => void) | null>(null);
  const toRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  const startedOnceRef = useRef(false);
  const firstWaitMsRef = useRef<number | null>(null);

  const disarmGesture = () => {
    const handler = resumeOnceRef.current;
    if (handler) {
      window.removeEventListener('pointerdown', handler, true);
      window.removeEventListener('keydown', handler, true);
      window.removeEventListener('touchstart', handler, true);
      window.removeEventListener('click', handler, true);
      resumeOnceRef.current = null;
    }
    gestureArmedRef.current = false;
  };

  const resumeSharedCtx = async () => {
    try {
      const mod = await import('../hooks/mixEngine/ctxSingleton');
      const ctx = mod.getCtx?.();
      if (ctx && ctx.state === 'suspended') { try { await ctx.resume(); } catch {} }
    } catch {}
  };

  const resumeAnalyserCtx = async () => {
    try {
      const an = mix?.getMasterAnalyser?.();
      const ctx = an?.context as AudioContext | undefined;
      if (ctx && ctx.state === 'suspended') { try { await ctx.resume(); } catch {} }
    } catch {}
  };

  const preflightReset = async () => {
    try { await (mix as any)?.preflightResetIfNeeded?.(); } catch {}
  };

  const tryPlay = async () => {
    try {
      await resumeSharedCtx();
      await resumeAnalyserCtx();
      await preflightReset();
      await mix?.play?.();
    } catch (e) {
      throw e;
    }
  };

  const armGestureAutoplay = () => {
    if (gestureArmedRef.current) return;
    gestureArmedRef.current = true;

    const resume = async () => {
      try {
        await tryPlay();
      } catch {}
      finally {
        disarmGesture();
      }
    };

    const handler = () => { void resume(); };
    resumeOnceRef.current = handler;
    window.addEventListener('pointerdown', handler, true);
    window.addEventListener('keydown', handler, true);
    window.addEventListener('touchstart', handler, true);
    window.addEventListener('click', handler, true);
  };

  // Main effect: scope selection -> try autoplay; if blocked/slow -> arm gesture
  useEffect(() => {
    const list = tracks || [];
    if (!scopeId || list.length === 0) return;

    if (autoPlayedScopeRef.current === scopeId) return;
    autoPlayedScopeRef.current = scopeId;

    // Reset state for the new scope
    startedOnceRef.current = false;
    firstWaitMsRef.current = null;
    try { clearAutoplayTimers(); } catch {}

    // Kick off autoplay attempt next tick
    toRef.current = setTimeout(() => {
      L.info('media:transport', 'autoplay.try', { scope: String(scopeId), tracks: list.length });
      try { clearAutoplayTimers(); } catch {}

      void tryPlay().catch(() => { armGestureAutoplay(); });

      // After a short delay, check if context actually running; if not, arm gesture
      const check = setTimeout(() => {
        try {
          const an = mix?.getMasterAnalyser?.();
          const ctxState = an?.context?.state as AudioContextState | undefined;
          if (ctxState !== 'running') {
            L.warn('media:health', 'autoplay.blocked', { ctxState });
            armGestureAutoplay();
          }
        } catch {}
      }, 300);

      // Health log: stop after context runs
      const clear = setInterval(() => {
        try {
          const an = mix?.getMasterAnalyser?.();
          if (an && an.context?.state === 'running') {
            L.info('media:transport', 'autoplay.ok');
            clearInterval(clear);
          }
        } catch {}
      }, 500);

      (window as any).__mediaAutoplayTimers ??= [];
      (window as any).__mediaAutoplayTimers.push(check, clear);
    }, 0);

    // rAF: monitor first meaningful advance to avoid re-arming
    const loop = () => {
      try {
        const status = mix?.status as string | undefined;
        const t = Number(mix?.currentTime) || 0;
        const ctxState: AudioContextState | undefined = (() => {
          try { return mix?.getMasterAnalyser?.()?.context?.state as AudioContextState | undefined; } catch { return undefined; }
        })();

        if (!startedOnceRef.current) {
          const isPlaying = status === 'playing';
          const contextRunning = ctxState === 'running';
          const advanced = t > 0; // any positive movement is enough to mark start here
          if (isPlaying && contextRunning && advanced) {
            startedOnceRef.current = true;
            disarmGesture();
          } else {
            if (ctxState === 'suspended') {
              armGestureAutoplay();
            } else {
              if (firstWaitMsRef.current == null) firstWaitMsRef.current = Date.now();
              const waited = Date.now() - (firstWaitMsRef.current || Date.now());
              if (waited > maxStartMs) {
                armGestureAutoplay();
              }
            }
          }
        }
      } catch {}
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      if (toRef.current) clearTimeout(toRef.current);
      toRef.current = null;
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      disarmGesture();
      try { clearAutoplayTimers(); } catch {}
    };
  }, [scopeId, tracks, mix, maxStartMs]);
}
